import React, { useState } from 'react';
import { Box, Typography, Button, Grid } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import ImportHubspotList from './ImportHubspotList';
import ImportHubspotSegments from './ImportHubspotSegments';
import { EnumProspectType } from '../../../types/prospects';
import { secondaryActionButtonSx } from '../../modal/ModalActionButtons';

interface Props {
  onConfirm: (rows: any[]) => void;
  onCancel: () => void;
  prospectType: EnumProspectType;
  hideCancel?: boolean;
}

type ImportType = 'COMPANIES' | 'CONTACTS' | 'SEGMENTS' | null;

interface SourceOption {
  id: ImportType;
  label: string;
  caption: string;
  icon: React.ReactNode;
}

const ImportHubspot: React.FC<Props> = ({
  onConfirm,
  onCancel,
  prospectType,
  hideCancel = false
}) => {
  const [importType, setImportType] = useState<ImportType>(null);

  const handleBack = () => {
    if (importType) {
      setImportType(null);
    } else {
      onCancel();
    }
  };

  const showAccountImport = prospectType === EnumProspectType.ACCOUNT;
  const showLeadImport = prospectType === EnumProspectType.LEAD;

  const sourceOptions: SourceOption[] = [
    ...(showAccountImport
      ? [
          {
            id: 'COMPANIES' as const,
            label: 'Companies',
            caption: 'Import from a companies list',
            icon: <BusinessIcon sx={{ fontSize: 24 }} />
          }
        ]
      : []),
    ...(showLeadImport
      ? [
          {
            id: 'CONTACTS' as const,
            label: 'Contacts',
            caption: 'Import from a contacts list',
            icon: <PersonIcon sx={{ fontSize: 24 }} />
          }
        ]
      : []),
    {
      id: 'SEGMENTS' as const,
      label: 'Segments',
      caption: 'Import from HubSpot segments',
      icon: <GroupWorkIcon sx={{ fontSize: 24 }} />
    }
  ];

  // Show selection screen if no type selected
  if (!importType) {
    return (
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography variant="body2" color="text.secondary">
          Choose where to import from
        </Typography>
        <Grid container spacing={1.5}>
          {sourceOptions.map((opt) => (
            <Grid item xs={6} key={opt.id}>
              <Box
                component="button"
                onClick={() => setImportType(opt.id)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  width: '100%',
                  minHeight: 100,
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  cursor: 'pointer',
                  bgcolor: 'background.paper',
                  textAlign: 'center',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: (theme) =>
                      theme.customColors?.themePrimary ?? 'primary.main',
                    bgcolor: 'action.hover'
                  }
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'action.hover',
                    color: (theme) =>
                      theme.customColors?.themePrimary ?? 'primary.main'
                  }}
                >
                  {opt.icon}
                </Box>
                <Typography variant="subtitle2" fontWeight={600}>
                  {opt.label}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ lineHeight: 1.2 }}
                >
                  {opt.caption}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
        {!hideCancel && (
          <Box display="flex" justifyContent="flex-end" sx={{ mt: 0.5 }}>
            <Button
              variant="text"
              onClick={onCancel}
              sx={secondaryActionButtonSx}
            >
              Cancel
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  // Render segments modal
  if (importType === 'SEGMENTS') {
    return (
      <ImportHubspotSegments
        onConfirm={onConfirm}
        onCancel={handleBack}
        hideCancel={hideCancel}
      />
    );
  }

  // Render the unified component with the selected type for COMPANIES/CONTACTS
  return (
    <ImportHubspotList
      importType={importType}
      onConfirm={onConfirm}
      onCancel={handleBack}
      hideCancel={hideCancel}
    />
  );
};

export default ImportHubspot;
